import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import { PageHeader, TableShell, EmptyState, StatCard } from "@/components/ui-kit";
import { IconBtn } from "@/pages/Clients";
import { CreditCard, Trash2, DollarSign, AlertCircle, CheckCircle2 } from "lucide-react";

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const { t } = useI18n();

  const load = () => Promise.all([
    api.get("/payments").then(({ data }) => setPayments(data)),
    api.get("/invoices").then(({ data }) => setInvoices(data)),
  ]);
  useEffect(() => { load(); }, []);

  const totalPaid = payments.reduce((a, p) => a + Number(p.amount || 0), 0);
  const outstanding = invoices.reduce((a, i) => a + Number(i.balance_due || 0), 0);
  const paidCount = invoices.filter((i) => i.status === "Paid").length;

  const remove = async (id) => {
    if (!window.confirm("Delete this payment? The invoice balance will be recalculated.")) return;
    await api.delete(`/payments/${id}`); toast.success(t("Payment deleted")); load();
  };

  return (
    <div data-testid="payments-page">
      <PageHeader title="Payments" subtitle="Track paid, outstanding, methods and payment dates." />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={DollarSign} label={t("Total Collected")} value={money(totalPaid)} accent="#16a34a" testid="pay-stat-collected" />
        <StatCard icon={AlertCircle} label={t("Outstanding")} value={money(outstanding)} accent="#ef4444" testid="pay-stat-outstanding" />
        <StatCard icon={CheckCircle2} label={t("Invoices Paid")} value={paidCount} accent="#5A189A" testid="pay-stat-paid" />
      </div>

      {payments.length === 0 ? (
        <EmptyState icon={CreditCard} title="No payments recorded" subtitle="Record payments from the Invoices page. They will appear here." />
      ) : (
        <TableShell headers={["Date", "Invoice", "Customer", "Method", "Amount", ""]} testid="payments-table">
          {payments.map((p) => (
            <tr key={p.id} className="border-b border-[#f4f0fa] hover:bg-[#faf8fd]">
              <td className="py-3.5 px-4 text-sm">{p.date}</td>
              <td className="py-3.5 px-4 text-sm font-medium text-[#5A189A]">{p.invoice_number}</td>
              <td className="py-3.5 px-4 text-sm">{p.customer_name}</td>
              <td className="py-3.5 px-4 text-sm">{p.method}</td>
              <td className="py-3.5 px-4 font-medium text-green-600">{money(p.amount)}</td>
              <td className="py-3.5 px-4"><IconBtn onClick={() => remove(p.id)} title="Delete" danger><Trash2 size={16} /></IconBtn></td>
            </tr>
          ))}
        </TableShell>
      )}
    </div>
  );
}
